import { createFileRoute } from "@tanstack/react-router";
import { Calendar, Clock } from "lucide-react";
import { getPosts, BLOG_UI, type Locale } from "@/lib/blog-posts-i18n";

const SITE = "https://kentro.com.br";

const META = {
  en: {
    title: "Kentro News — Blog by Kentro Sistemas",
    desc: "Insights on AI, omnichannel service, sales and marketing for modern companies.",
    ogTitle: "Kentro News — Blog",
    ogDesc: "Insights on AI, omnichannel service and revenue growth.",
  },
  es: {
    title: "Kentro News — Blog de Kentro Sistemas",
    desc: "Contenido sobre IA, atención omnicanal, ventas y marketing para empresas modernas.",
    ogTitle: "Kentro News — Blog",
    ogDesc: "Ideas sobre IA, atención omnicanal y crecimiento de ingresos.",
  },
} as const;

export const Route = createFileRoute("/$locale/blog")({
  head: ({ params }) => {
    const loc: Locale = params.locale === "es" ? "es" : "en";
    const m = META[loc];
    const posts = getPosts(loc);
    return {
      meta: [
        { title: m.title },
        { name: "description", content: m.desc },
        { property: "og:title", content: m.ogTitle },
        { property: "og:description", content: m.ogDesc },
        { property: "og:url", content: `${SITE}/${loc}/blog` },
        { property: "og:locale", content: loc === "es" ? "es_ES" : "en_US" },
      ],
      links: [
        { rel: "canonical", href: `${SITE}/${loc}/blog` },
        { rel: "alternate", hrefLang: "pt-BR", href: `${SITE}/blog` },
        { rel: "alternate", hrefLang: "en", href: `${SITE}/en/blog` },
        { rel: "alternate", hrefLang: "es", href: `${SITE}/es/blog` },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE}/blog` },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Kentro News",
            url: `${SITE}/${loc}/blog`,
            inLanguage: loc === "es" ? "es" : "en",
            blogPost: posts.map((p) => ({
              "@type": "BlogPosting",
              headline: p.title,
              url: `${SITE}/${loc}/blog/${p.slug}`,
              datePublished: p.date,
              author: { "@type": "Organization", name: p.author },
            })),
          }),
        },
      ],
    };
  },
  component: BlogPage,
});

function BlogPage() {
  const { locale: rawLocale } = Route.useParams();
  const locale: Locale = rawLocale === "es" ? "es" : "en";
  const ui = BLOG_UI[locale];
  const posts = [...getPosts(locale)].sort((a, b) => (a.date < b.date ? 1 : -1));

  return (
    <>
      <section className="gradient-hero relative overflow-hidden pt-24 pb-12">
        <div className="circuit-pattern absolute inset-0 opacity-30" />
        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold tracking-tight md:text-5xl">
            Kentro <span className="gradient-text">News</span>
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-lg text-muted-foreground">{ui.heroSubtitle}</p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold tracking-tight md:text-3xl">{ui.featured}</h2>
        <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {posts.map((p) => (
            <a
              key={p.slug}
              href={`/${locale}/blog/${p.slug}`}
              className="group flex flex-col rounded-2xl border border-border bg-card/40 p-6 transition hover:border-primary-glow/40"
            >
              <span className="inline-flex w-fit rounded-full border border-border bg-background/40 px-2.5 py-1 text-xs text-muted-foreground">
                {p.category}
              </span>
              <h3 className="mt-4 text-lg font-semibold leading-snug group-hover:text-primary-glow">
                {p.title}
              </h3>
              <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{p.description}</p>
              <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Calendar className="size-3.5" />
                  {new Date(p.date).toLocaleDateString(ui.dateLocale, {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="size-3.5" /> {p.readingMinutes} {ui.minutes}
                </span>
              </div>
            </a>
          ))}
        </div>
      </section>

    </>
  );
}
